import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "../assets/images/white_logo.svg";

export const Survey = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Extract URL parameters
  const email = searchParams.get('email');
  const age = searchParams.get('age');
  const location = searchParams.get('location');
  const gender = searchParams.get('gender');

  // Check if user has valid parameters, redirect if not
  useEffect(() => {
    if (!email || !age || !location || !gender) {
      // Redirect to home if required parameters are missing
      navigate('/', { replace: true });
      return;
    }
    
    // Clean URL to show only /survey
    const newUrl = window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [email, age, location, gender, navigate]);

  const [formData, setFormData] = useState({
    reason: "",
    hopes: "",
    connection: "",
    stay: "",
    comeback: "",
  });
  const [progress, setProgress] = useState(0);
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const totalQuestions = 5;
    const answeredQuestions = Object.values(formData).filter(
      (value) => value !== ""
    ).length;
    const progressPercentage = (answeredQuestions / totalQuestions) * 100;
    setProgress(progressPercentage);

    const allFieldsFilled = Object.values(formData).every(
      (value) => value !== ""
    );
    setIsFormComplete(allFieldsFilled);
  }, [formData]);

  const handleRadioChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTextareaChange = (e, name) => {
    const value = e.target.value;
    
    // Prevent leading spaces and check if the trimmed value is empty
    if (value.trim() === '' && value !== '') {
      return; // Don't update state if only spaces are entered
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const payload = {
        email: email, // Use email from URL parameter
        age: age, // Include age from URL parameter
        gender: gender, // Include gender from URL parameter
        location: location, // Include location from URL parameter
        answers: [
          formData.reason,
          formData.hopes,
          formData.connection,
          formData.stay,
          formData.comeback,
        ],
      };

      const response = await fetch(
        "https://dev.mixxerco.com/api/app/delete/feedback",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log("API Response:", result);
        setShowThankYou(true);
      } else {
        console.error("API Error:", response.status, response.statusText);
        alert("There was an error submitting your feedback. Please try again.");
      }
    } catch (error) {
      console.error("Network Error:", error);
      alert(
        "There was a network error. Please check your connection and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  // Don't render if required parameters are missing
  if (!email || !age || !location || !gender) {
    return null;
  }

  const ThankYouPage = () => (
    <div
      className="mobile-form-content"
      style={{
        padding: "3rem 4rem 0 4rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        minHeight: "calc(100vh - 200px)",
      }}
    >
      <div style={{ marginBottom: "2rem" }}>
        <img src="Thank.svg" alt="" />
      </div>

      <h2
        className="mobile-question-title"
        style={{
          fontSize: "2rem",
          fontWeight: "700",
          color: "#333",
          marginBottom: "1rem",
        }}
      >
        Thank You for Helping Us Improve.
      </h2>

      <p
        className="mobile-question-subtitle"
        style={{
          color: "#666",
          fontSize: "1.1rem",
          lineHeight: "1.6",
          marginBottom: "1rem",
          maxWidth: "400px",
        }}
      >
        We're sorry to see you go, but your input will help us improve and make
        Mixer better for everyone. Wishing you all the best!
      </p>

      <button
        onClick={handleBackToHome}
        style={{
          backgroundColor: "#988265",
          color: "white",
          border: "none",
          padding: "0.75rem 2rem",
          borderRadius: "10px",
          fontSize: "1rem",
          fontWeight: "500",
          cursor: "pointer",
          transition: "background-color 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = "#87745d";
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = "#988265";
        }}
      >
        Back to Home
      </button>
    </div>
  );

  return (
    <>
      <style>{`
        @media (max-width: 1366px) and (min-width: 769px) {
          .mobile-navbar {
            padding: 10px 20px !important;
            height: 60px !important;
          }
          
          .mobile-logo {
            margin-left: 0 !important;
            width: 120px !important;
            height: 22px !important;
          }
          
          .mobile-nav-links {
            display: none !important;
          }
          
          .mobile-main-container {
            margin-top: 60px !important;
            flex-direction: column !important;
          }
          
          .mobile-image-section {
            position: relative !important;
            width: 100% !important;
            height: 160px !important;
            top: 0 !important;
            left: 0 !important;
            flex: none !important;
            margin-left: 0 !important;
            display: ${showThankYou ? "none" : "block"} !important;
          }
          
          .mobile-form-section {
            margin-left: 0 !important;
            width: 100% !important;
            height: auto !important;
            flex: 1 !important;
          }
          
          .mobile-form-content {
            padding: 2rem 1.5rem 0 1.5rem !important;
          }
          
          .mobile-form-footer {
            padding: 1rem 1.5rem 2rem 1.5rem !important;
            position: relative !important;
            width: 100% !important;
            right: auto !important;
            display: ${showThankYou ? "none" : "block"} !important;
          }
          
          .mobile-question-title {
            font-size: 1.5rem !important;
            font-weight: 900 !important;
          }
          
          .mobile-question-subtitle {
            font-size: 1rem !important;
          }
          
          .mobile-question-text {
            font-size: 1.1rem !important;
          }
          
          .mobile-footer {
            display: ${showThankYou ? "none !important" : "block !important"};
          }
          
          .desktop-form-footer {
            display: none !important;
          }
        }

        @media (max-width: 768px) {
          .mobile-navbar {
            padding: 10px 20px !important;
            height: 60px !important;
          }
          
          .mobile-logo {
            margin-left: 0 !important;
            width: 120px !important;
            height: 22px !important;
          }
          
          .mobile-nav-links {
            display: none !important;
          }
          
          .mobile-main-container {
            margin-top: 60px !important;
            flex-direction: column !important;
          }
          
          .mobile-image-section {
            position: relative !important;
            width: 100% !important;
            height: 240px !important;
            top: 0 !important;
            left: 0 !important;
            flex: none !important;
            margin-left: 0 !important;
            display: ${showThankYou ? "none" : "block"} !important;
          }
          
          .mobile-form-section {
            margin-left: 0 !important;
            width: 100% !important;
            height: auto !important;
            flex: 1 !important;
          }
          
          .mobile-form-content {
            padding: 2rem 1.5rem 0 1.5rem !important;
          }
          
          .mobile-form-footer {
            padding: 1rem 1.5rem 2rem 1.5rem !important;
            position: relative !important;
            width: 100% !important;
            right: auto !important;
            display: ${showThankYou ? "none" : "block"} !important;
          }
          
          .mobile-question-title {
            font-size: 1.5rem !important;
            font-weight: 900 !important;
          }
          
          .mobile-question-subtitle {
            font-size: 1rem !important;
          }
          
          .mobile-question-text {
            font-size: 1.1rem !important;
          }
          
          .mobile-footer {
            display: ${showThankYou ? "none !important" : "block !important"};
          }
          
          .desktop-form-footer {
            display: none !important;
          }
        }
        
        @media (max-width: 480px) {
          .mobile-image-section {
            height: 240px !important;
            display: ${showThankYou ? "none" : "block"} !important;
          }
          
          .mobile-form-content {
            padding: 1.5rem 1rem 0 1rem !important;
          }
          
          .mobile-form-footer {
            padding: 1rem 1rem 2rem 1rem !important;
            display: ${showThankYou ? "none" : "block"} !important;
          }
          
          .mobile-question-title {
            font-size: 1.3rem !important;
            font-weight: 900 !important;
          }
          
          .mobile-question-subtitle {
            font-size: 0.95rem !important;
          }
          
          .mobile-question-text {
            font-size: 1rem !important;
          }
          
          .mobile-footer {
            display: ${showThankYou ? "none !important" : "block !important"};
          }
          
          .desktop-form-footer {
            display: none !important;
          }
        }
      `}</style>

      <nav
        className="mobile-navbar"
        style={{
          backgroundColor: "white",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "white",
          width: "100%",
          position: "fixed",
          top: 0,
          zIndex: 1000,
          height: "90px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div
          className="mobile-logo"
          style={{
            filter:
              "brightness(0) saturate(100%) invert(63%) sepia(12%) saturate(765%) hue-rotate(358deg) brightness(92%) contrast(87%)",
            marginLeft: "100px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
          onClick={() => handleNavigation("/")}
        >
          <img
            src={logo}
            alt="Logo"
            width={158}
            height={30}
            className="mx-auto"
          />
        </div>

        <ul
          className="mobile-nav-links"
          style={{
            display: "flex",
            flexDirection: "row",
            listStyle: "none",
            margin: 0,
            padding: 0,
            gap: "5rem",
            alignItems: "center",
            height: "100%",
            marginRight: "180px",
          }}
        >
          {["Home", "Features", "About", "FAQ's", "Contact"].map((item) => (
            <li
              key={item}
              style={{ height: "100%", display: "flex", alignItems: "center" }}
            >
              <button
                onClick={() => handleNavigation("/")}
                style={{
                  color: "#655f5f",
                  fontWeight: "400",
                  fontSize: "1.2rem",
                  padding: "1rem 0",
                  textDecoration: "none",
                  fontFamily: "Arial, sans-serif",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {item}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div
        className="mobile-main-container"
        style={{
          display: "flex",
          minHeight: "90vh",
          fontFamily: "Arial, sans-serif",
          marginTop: "90px",
        }}
      >
        <div
          className="mobile-image-section"
          style={{
            flex: "0 0 50%",
            position: "fixed",
            top: "90px",
            left: 0,
            width: "50%",
            height: "calc(100vh - 90px)",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src="M.png"
            alt="People enjoying mixer event"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover"
            }}
          />
        </div>

        <div
          className="mobile-form-section"
          style={{
            flex: "0 0 50%",
            marginLeft: "50%",
            backgroundColor: "#f8f9fa",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showThankYou ? (
            <ThankYouPage />
          ) : (
            <>
              <div
                style={{
                  flex: "1 1 auto",
                  overflowY: "auto",
                  paddingBottom: "120px",
                }}
              >
                <div
                  className="mobile-form-content"
                  style={{
                    padding: "3rem 4rem 0 4rem",
                  }}
                >
                  <h2
                    className="mobile-question-title"
                    style={{
                      fontSize: "2rem",
                      fontWeight: "700",
                      color: "#333",
                      marginBottom: "1.5rem",
                    }}
                  >
                    We're Sorry to See You Go!
                  </h2>

                  <p
                    className="mobile-question-subtitle"
                    style={{
                      color: "#666",
                      fontSize: "1.1rem",
                      lineHeight: "1.6",
                      marginBottom: "2.5rem",
                    }}
                  >
                    Before you leave, we'd love to know why. Your feedback will
                    help us improve Mixer and create a better experience for
                    everyone. This will only take a minute.
                  </p>

                  <div style={{ marginBottom: "2.5rem" }}>
                    <h3
                      className="mobile-question-text"
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                        color: "#333",
                        marginBottom: "1.5rem",
                        marginLeft: "10px",
                      }}
                    >
                      1. What made you decide to delete your Mixer account today?
                    </h3>

                    {[
                      "I couldn't find the kind of people I wanted to meet.",
                      "Not enough Mixers near me.",
                      "I didn't feel safe or comfortable.",
                      "The app was confusing or buggy.",
                      "I didn't have time to use it.",
                      "I expected something different.",
                      "I just wanted to try it briefly",
                      "Other reasons",
                    ].map((option, index) => (
                      <label
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          marginBottom: "1.2rem",
                          marginLeft: "10px",
                          cursor: "pointer",
                          width: "100%",
                        }}
                      >
                        <input
                          type="radio"
                          name="reason"
                          value={option}
                          checked={formData.reason === option}
                          onChange={() => handleRadioChange("reason", option)}
                          style={{
                            marginRight: "1rem",
                            transform: "scale(1.2)",
                            marginTop: "0.2rem",
                            accentColor: "#7E6C54",
                          }}
                        />
                        <span
                          style={{
                            color: "#666",
                            fontSize: "1rem",
                            lineHeight: "1.5",
                          }}
                        >
                          {option}
                        </span>
                      </label>
                    ))}
                  </div>

                  <div style={{ marginBottom: "2.5rem" }}>
                    <h3
                      className="mobile-question-text"
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                        color: "#333",
                        marginBottom: "1.5rem",
                        marginLeft: "10px",
                      }}
                    >
                      2. What were you hoping to get out of Mixer that you didn't?
                    </h3>

                    <textarea
                      placeholder="Write here..."
                      value={formData.hopes}
                      onChange={(e) => handleTextareaChange(e, "hopes")}
                      onKeyDown={(e) => {
                        // Prevent space if current value is empty
                        if (e.key === ' ' && !e.target.value.trim()) {
                          e.preventDefault();
                        }
                      }}
                      style={{
                        width: "100%",
                        height: "120px",
                        padding: "1.2rem",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        fontSize: "1rem",
                        fontFamily: "Arial, sans-serif",
                        resize: "vertical",
                        marginBottom: "0.5rem",
                        boxSizing: "border-box",
                        marginLeft: "10px",
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: "2.5rem" }}>
                    <h3
                      className="mobile-question-text"
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                        color: "#333",
                        marginBottom: "1.5rem",
                        marginLeft: "10px",
                      }}
                    >
                      3. Did you feel a genuine connection with anyone through Mixer?
                    </h3>

                    {[
                      "Yes, I made one or more real friends.",
                      "Sort of—I met people but didn't click deeply.",
                      "No, I didn't connect with anyone.",
                      "I never actually went to a Mixer.",
                    ].map((option, index) => (
                      <label
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          marginBottom: "1.2rem",
                          marginLeft: "10px",
                          cursor: "pointer",
                          width: "100%",
                        }}
                      >
                        <input
                          type="radio"
                          name="connection"
                          value={option}
                          checked={formData.connection === option}
                          onChange={() => handleRadioChange("connection", option)}
                          style={{
                            marginRight: "1rem",
                            transform: "scale(1.2)",
                            marginTop: "0.2rem",
                            accentColor: "#7E6C54",
                          }}
                        />
                        <span
                          style={{
                            color: "#666",
                            fontSize: "1rem",
                            lineHeight: "1.5",
                          }}
                        >
                          {option}
                        </span>
                      </label>
                    ))}
                  </div>

                  <div style={{ marginBottom: "2.5rem" }}>
                    <h3
                      className="mobile-question-text"
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                        color: "#333",
                        marginBottom: "1.5rem",
                        marginLeft: "10px",
                      }}
                    >
                      4. What would have made you stay?
                    </h3>
                   
                    <textarea
                      placeholder="Write here..."
                      value={formData.stay}
                      onChange={(e) => handleTextareaChange(e, "stay")}
                      onKeyDown={(e) => {
                        // Prevent space if current value is empty
                        if (e.key === ' ' && !e.target.value.trim()) {
                          e.preventDefault();
                        }
                      }}
                      style={{
                        width: "100%",
                        height: "120px",
                        padding: "1.2rem",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        fontSize: "1rem",
                        fontFamily: "Arial, sans-serif",
                        resize: "vertical",
                        marginBottom: "0.5rem",
                        boxSizing: "border-box",
                        marginLeft: "10px",
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: "2.5rem" }}>
                    <h3
                      className="mobile-question-text"
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                        color: "#333",
                        marginBottom: "1.5rem",
                        marginLeft: "10px",
                      }}
                    >
                      5. Would you be open to coming back in the future if we fixed your issue or launched new features?
                    </h3>
                    {["Yes", "Maybe", "No"].map((option, index) => (
                      <label
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          marginBottom: "1.2rem",
                          marginLeft: "10px",
                          cursor: "pointer",
                          width: "100%",
                        }}
                      >
                        <input
                          type="radio"
                          name="comeback"
                          value={option}
                          checked={formData.comeback === option}
                          onChange={() => handleRadioChange("comeback", option)}
                          style={{
                            marginRight: "1rem",
                            transform: "scale(1.2)",
                            marginTop: "0.2rem",
                            accentColor: "#7E6C54",
                          }}
                        />
                        <span
                          style={{
                            color: "#666",
                            fontSize: "1rem",
                            lineHeight: "1.5",
                          }}
                        >
                          {option}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div
                className="desktop-form-footer"
                style={{
                  padding: "1rem 4rem 2rem 4rem",
                  backgroundColor: "#f8f9fa",
                  borderTop: "1px solid #dee2e6",
                  position: "fixed",
                  bottom: 0,
                  right: 0,
                  width: "50%",
                  zIndex: 100,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "0",
                  }}
                >
                  <div
                    style={{
                      flexGrow: 1,
                      marginRight: "1rem",
                    }}
                  >
                    <h3
                      style={{
                        color: "#333",
                        fontSize: "1.25rem",
                        margin: "0 0 0.5rem 0",
                      }}
                    >
                      {Math.round(progress)}% Complete
                    </h3>
                    <div
                      style={{
                        height: "10px",
                        backgroundColor: "#e9ecef",
                        position: "relative",
                        borderRadius: "0",
                      }}
                    >
                      <div
                        style={{
                          width: `${progress}%`,
                          height: "100%",
                          backgroundColor: "#988265",
                          position: "absolute",
                          top: 0,
                          left: 0,
                          transition: "width 0.3s ease",
                          borderRadius: "3px",
                        }}
                      ></div>
                    </div>
                  </div>
                  <button
                    onClick={handleSubmit}
                    disabled={!isFormComplete || isSubmitting}
                    style={{
                      backgroundColor: isSubmitting
                        ? "#988265"
                        : isFormComplete
                        ? "#988265"
                        : "#655f5f",
                      color: "white",
                      border: "none",
                      width: "20%",
                      padding: "0.75rem 2rem",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      fontWeight: "500",
                      cursor:
                        isFormComplete && !isSubmitting
                          ? "pointer"
                          : "not-allowed",
                      transition: "background-color 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div
        style={{
          display: showThankYou ? "none" : "none",
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "#f8f9fa",
          borderTop: "1px solid #dee2e6",
          padding: "1rem 1.5rem 2rem 1.5rem",
          zIndex: 100,
        }}
        className="mobile-footer"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "0",
          }}
        >
          <div
            style={{
              flexGrow: 1,
              marginRight: "1rem",
            }}
          >
            <h3
              style={{
                color: "#333",
                fontSize: "1.25rem",
                margin: "0 0 0.5rem 0",
              }}
            >
              {Math.round(progress)}% Complete
            </h3>
            <div
              style={{
                height: "10px",
                backgroundColor: "#e9ecef",
                position: "relative",
                borderRadius: "0",
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  backgroundColor: "#988265",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  transition: "width 0.3s ease",
                  borderRadius: "3px",
                }}
              ></div>
            </div>
          </div>
          <button
            onClick={handleSubmit}
            disabled={!isFormComplete || isSubmitting}
            style={{
              backgroundColor: isSubmitting
                ? "#988265"
                : isFormComplete
                ? "#988265"
                : "#655f5f",
              color: "white",
              border: "none",
              padding: "0.75rem 2rem",
              borderRadius: "8px",
              fontSize: "1rem",
              fontWeight: "500",
              cursor:
                isFormComplete && !isSubmitting ? "pointer" : "not-allowed",
              transition: "background-color 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </>
  );
};